import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { SERVICES } from "@/components/Services";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  child_name: "",
  child_age: "",
  parent_name: "",
  phone: "",
  email: "",
  service: "",
  preferred_date: null,
  notes: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.child_name.trim()) return "Child's name is required";
    if (!form.child_age.trim()) return "Child's age is required";
    if (!form.parent_name.trim()) return "Parent's name is required";
    if (!/^[+()0-9\s-]{6,20}$/.test(form.phone)) return "Enter a valid phone number";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid email";
    if (!form.service) return "Select a service";
    if (!form.preferred_date) return "Choose a preferred date";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_date: format(form.preferred_date, "yyyy-MM-dd"),
      };
      await axios.post(`${API}/appointments`, payload);
      setSubmitted(true);
      toast.success("Appointment request sent. We'll be in touch shortly.");
      setForm(initial);
    } catch (e2) {
      const msg =
        e2?.response?.data?.detail?.[0]?.msg ||
        e2?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="booking-success"
        className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center"
      >
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#E6F5EE] text-[#0055FF]">
          <Send className="h-6 w-6" />
        </div>
        <h3 className="mt-4 font-heading text-2xl font-semibold text-[#0A192F]">
          Request received.
        </h3>
        <p className="mt-2 text-[#475569]">
          Our team will contact you within 24 hours to confirm your appointment.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          data-testid="booking-another-btn"
          className="mt-6 inline-flex items-center bg-[#0A192F] text-white hover:bg-black rounded-full px-5 py-2.5 font-medium text-sm transition-colors"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="booking-form"
      className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Child's Name" required>
          <Input
            data-testid="input-child-name"
            value={form.child_name}
            onChange={(e) => update("child_name", e.target.value)}
            placeholder="e.g., Aarav Sharma"
          />
        </Field>
        <Field label="Child's Age" required>
          <Input
            data-testid="input-child-age"
            value={form.child_age}
            onChange={(e) => update("child_age", e.target.value)}
            placeholder="e.g., 4 years"
          />
        </Field>
        <Field label="Parent's / Guardian's Name" required>
          <Input
            data-testid="input-parent-name"
            value={form.parent_name}
            onChange={(e) => update("parent_name", e.target.value)}
            placeholder="Your full name"
          />
        </Field>
        <Field label="Phone" required>
          <Input
            data-testid="input-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="+91 98xxxxxxxx"
          />
        </Field>
        <Field label="Email" required>
          <Input
            data-testid="input-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Service Required" required>
          <Select
            value={form.service}
            onValueChange={(v) => update("service", v)}
          >
            <SelectTrigger data-testid="select-service">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem
                  key={s.key}
                  value={s.title}
                  data-testid={`service-option-${s.key}`}
                >
                  {s.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Preferred Date" required>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="date-picker-btn"
                className={cn(
                  "w-full inline-flex items-center justify-between rounded-md border border-[#E2E8F0] bg-white px-3 py-2 text-sm hover:border-[#0055FF] transition-colors",
                  !form.preferred_date && "text-[#94A3B8]"
                )}
              >
                {form.preferred_date
                  ? format(form.preferred_date, "PPP")
                  : "Pick a date"}
                <CalendarIcon className="h-4 w-4 text-[#475569]" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={form.preferred_date}
                onSelect={(d) => update("preferred_date", d)}
                disabled={(d) =>
                  d < new Date(new Date().setHours(0, 0, 0, 0))
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </Field>
        <Field label="Notes (Optional)" full>
          <Textarea
            data-testid="input-notes"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="Anything we should know before the consultation?"
            rows={4}
          />
        </Field>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-xs text-[#475569] max-w-md">
          By submitting, you agree to be contacted by SHUNYA CDC to confirm your
          consultation. We never share your data.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="booking-submit-btn"
          className="inline-flex items-center gap-2 bg-[#0055FF] text-white hover:bg-[#0044CC] disabled:opacity-70 rounded-full px-6 py-3 font-medium transition-colors"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              Book Consultation
              <Send className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={cn("space-y-1.5", full && "md:col-span-2")}>
      <Label className="text-[13px] font-medium text-[#0F172A]">
        {label} {required && <span className="text-[#EF4444]">*</span>}
      </Label>
      {children}
    </div>
  );
}
